import { Dictionary } from 'lodash';
import { MutableRefObject } from 'react';
import type { Optional } from 'utility-types';
import { PolicySubType } from 'api/ai-chat/types';
import type { DateType, Team, Role, User } from 'api/models';
import type {
  ProjectPhasePreview,
  ProjectPreview,
  TaskPreview,
} from 'api/notifications';
import { ProjectBudgetItem, ProjectBudgetItemLite } from 'api/project';
import type { Task as ITask } from 'api/tasks';
import {
  Field,
  FieldAccessMeta,
  FieldAccessMetaInBase3,
  FieldValueType,
} from 'api/tasks';
import {
  ApiTimesheetStatus,
  EntityToBudgetsMap,
  TimesheetCalendarSourceType,
  TimesheetCategory,
  TimesheetEntryType,
  TimesheetSourceType,
  TimesheetUISourceType,
} from 'api/timesheets';
import { TimesheetSourcePage } from 'api/timesheets/types';
import type { TimesheetActiveLocks } from 'api/timesheets-list/types';
import type { CustomOption } from 'components/atoms/select/types';
import { UserOption } from 'utils/project-fields/types';
import { InheritableFieldValue } from '../module-settings/fields/types';

export enum WeekPreset {
  ThisWeek = 'this-week',
  PreviousWeek = 'previous-week',
}

export enum WeekMenuActions {
  CopyFromSelectedWeek = 'copy_from_selected_week',
  CopyFromLastWeek = 'copy_from_last_week',
  AddFromAllocations = 'add_from_allocations',
}

export interface SelectedOption {
  value?: number | string;
  label?: string;
  type?: TimesheetEntryType;
}

export interface TaskOptionModalDetails {
  rowIndex?: number;
  newProjectId?: number;
  projectId?: number;
  taskId?: number;
}

export interface BulkActionDetails {
  row?: number;
  newProjectId?: number;
  newTask?: any;
}

export enum FilterViewType {
  ProjectsIOwn = 'projects_i_own',
  AllTimesheets = 'all_timesheets',
  Submitted = 'submitted',
  NotSubmitted = 'not_submitted',
  Approved = 'approved',
  Rejected = 'rejected',
  MyTeam = 'my_team',
}

interface RbacOptions {
  accessFlags?: string;
  activeLocks?: TimesheetActiveLocks[];
}

export interface TimeEntryTaskPreview extends TaskPreview {
  defaultBillable?: boolean;
  defaultCategoryId?: number;
  projectFinancialsBudgetId?: number;
  projectFinancialsBudgets?: ProjectBudgetItemLite[];
  // these are meant to always hold the original values form BE. Do not override these values anywhere
  originalDefaultCategoryId?: number;
  originalDefaultBillable?: boolean;
  effort?: number;
  timeEntryMinutes?: ITask['timeEntryMinutes'];
  deleted?: boolean;
}

export const FilterViewOptions = [
  FilterViewType.MyTeam,
  FilterViewType.AllTimesheets,
  FilterViewType.ProjectsIOwn,
  FilterViewType.Submitted,
  FilterViewType.NotSubmitted,
  FilterViewType.Approved,
  FilterViewType.Rejected,
];

export interface FilterItem extends CustomOption {
  count: number;
}

export enum TimeEntryState {
  NotCreated = 'NotCreated',
  Creating = 'Creating',
  PartiallyFilled = 'PartiallyFilled',
  Loaded = 'Loaded',
}

export interface PolicyConflictPreview {
  policyId: number;
  policySubType: PolicySubType;
}

export interface TimesheetTableTimeEntry extends RbacOptions {
  timeEntryId: number;
  activityName?: string;
  notes?: string;
  category?: TimesheetCategory;
  projectFinancialsBudgetId?: number;
  projectFinancialsBudget?: ProjectBudgetItem;
  billable: boolean;
  minutes: number;
  inState: TimeEntryState;
  disable?: boolean;
  roleId?: number;
  teamId?: number;
  invoiceId?: number;
  invoiceLineItemId?: number;
  policyConflicts?: PolicyConflictPreview[];
  fields?: Array<InheritableFieldValue<FieldValueType>>;
  fieldsAccessMeta?: FieldAccessMeta;
  fieldsAccessMetaInBase3?: FieldAccessMetaInBase3;
  approvalStatus?: ApiTimesheetStatus;
}

export interface TimesheetTask extends TimeEntryTaskPreview, RbacOptions {
  entryType: TimesheetEntryType.TaskEntry;
  sourceType: TimesheetSourceType;
  sourceId: number;
  approvalStatus: TimesheetStatus;
  id: number;
  invoiceId?: number;
  entityToBudgetsMap?: EntityToBudgetsMap;
  prefilledBudget?: number;
}

interface BaseTimesheetTask {
  activityName: string;
  project?: ProjectPreview;
  defaultBillable?: boolean;
  deleted?: boolean;
  defaultCategoryId?: number;
  projectFinancialsBudgetId?: number;
}
export interface TimesheetAdhocTask extends BaseTimesheetTask, RbacOptions {
  entryType: TimesheetEntryType.AdhocEntry;
  sourceType: TimesheetUISourceType;
  approvalStatus: TimesheetStatus;
  id?: number;
  invoiceId?: number;
  entityToBudgetsMap?: EntityToBudgetsMap;
  prefilledBudget?: number;
}

export interface TimesheetCalendarTask extends BaseTimesheetTask, RbacOptions {
  entryType: TimesheetEntryType.Integration;
  sourceType: TimesheetCalendarSourceType;
  sourceId: string;
  approvalStatus: TimesheetStatus;
  id?: number;
  invoiceId?: number;
  entityToBudgetsMap?: EntityToBudgetsMap;
  prefilledBudget?: number;
}

export interface ProjectRow extends RbacOptions {
  project: ProjectPreview;
  defaultCategoryId?: number | undefined;
  projectFinancialsBudgetId?: number;
  entryType: TimesheetEntryType.ProjectEntry;
  sourceId: number;
  sourceType: TimesheetSourceType;
  billable?: boolean;
  approvalStatus: TimesheetStatus;
  id?: number;
  invoiceId?: number;
  entityToBudgetsMap?: EntityToBudgetsMap;
  prefilledBudget?: number;
}

export interface PhaseTask extends RbacOptions {
  project: ProjectPreview;
  projectPhase: ProjectPhasePreview;
  entryType: TimesheetEntryType.PhaseEntry;
  defaultCategoryId?: number | undefined;
  projectFinancialsBudgetId?: number;
  projectFinancialsBudgets?: ProjectBudgetItemLite[];
  billable?: boolean;
  status?: number;
  sourceId: number;
  sourceType: TimesheetSourceType;
  private?: boolean;
  startDate?: DateType;
  dueDate?: DateType;
  approvalStatus: TimesheetStatus;
  id?: number;
  invoiceId?: number;
  entityToBudgetsMap?: EntityToBudgetsMap;
  prefilledBudget?: number;
}

export type TimesheetWeeklyRowTask =
  | TimesheetTask
  | TimesheetAdhocTask
  | TimesheetCalendarTask
  | ProjectRow
  | PhaseTask;

export type EntityTask = TimesheetTask | ProjectRow | PhaseTask;
export interface TimesheetWeeklyRow {
  task: TimesheetWeeklyRowTask;
  days: Record<string, TimesheetTableTimeEntry[]>;
}

export interface TimesheetWeekSummary {
  totalMinutesForDays: Record<string, number>;
  totalMinutesForWeek: number;
  totalBillableMinutesForWeek: number;
  totalNonBillableMinutesForWeek: number;
}

export interface TotalMinutes {
  totalBillableMinutes?: number;
  totalMinutes: number;
  totalNonBillableMinutes?: number;
}

export interface UserLevelSummary {
  user: User;
  userId: number;
  startDate: string;
  days: Record<string, TotalMinutes>;
  status: TimesheetStatus;
  updatedAt: number;
  lastReminderAt: number;
  projectId: number;
  projectName?: string;
  reminders: TimesheetReminder[];
  isAnyEntryInvoiced?: boolean;
  policyConflicts?: Dictionary<PolicyConflictPreview>;
  isAnyEntrySubmitted: boolean;
}

export enum TimesheetStatus {
  NotSubmitted = 'not_submitted',
  Submitted = 'submitted',
  Approved = 'approved',
  Rejected = 'rejected',
}

export interface TimesheetReminder {
  createdBy: number;
  createdAt: number;
}

export interface TimeEntryCustomFieldInputProps {
  field: Field;
  value: any;
  disabled: boolean;
  onChange: (value: any) => void;
  wrapperRef?: MutableRefObject<HTMLDivElement | null>;
  usersOptions?: UserOption[]; // Deprecated: use asyncUserOptions instead
  asyncUserOptions?: {
    defaultOptions: any[];
    loadOptions: (input: string) => Promise<any[]>;
  };
  autoFocus?: boolean;
}

export interface TimesheetTimeEntry {
  timeEntryId: number;
  date: string;
  notes?: string;
  category?: TimesheetCategory;
  projectFinancialsBudgetId?: number;
  projectFinancialsBudget?: ProjectBudgetItem;
  billable: boolean;
  minutes: number;
  userId: number;
  inState: TimeEntryState;
  status: ApiTimesheetStatus;
  disable?: boolean;
  roleId?: number;
  teamId?: number;
  invoiceId?: number;
  invoiceLineItemId?: number;
  accessFlags?: string;
  activeLocks?: TimesheetActiveLocks[];
  policyConflicts?: PolicyConflictPreview[];
  fields?: Array<InheritableFieldValue<FieldValueType>>;
  fieldsAccessMeta?: FieldAccessMeta;
  fieldsAccessMetaInBase3?: FieldAccessMetaInBase3;
  entityToBudgetsMap?: EntityToBudgetsMap;
  allocationId?: number;
}
export interface TimesheetTaskEntry extends TimesheetTimeEntry, RbacOptions {
  type: TimesheetEntryType.TaskEntry;
  task: TimeEntryTaskPreview;
  sourceId: number;
  sourceType: TimesheetUISourceType.Task;
  project: ProjectPreview;
  eventId?: string;
}

export interface TimesheetAdhocEntry extends TimesheetTimeEntry, RbacOptions {
  type: TimesheetEntryType.AdhocEntry;
  activityName: string;
  project: ProjectPreview;
  sourceType: TimesheetUISourceType.Adhoc;
}

export interface TimesheetProjectEntry extends TimesheetTimeEntry, RbacOptions {
  type: TimesheetEntryType.ProjectEntry;
  project: ProjectPreview;
  sourceId?: number;
  sourceType?: TimesheetUISourceType.Project;
}

export interface TimesheetPhaseEntry extends TimesheetTimeEntry, RbacOptions {
  type: TimesheetEntryType.PhaseEntry;
  projectPhase: ProjectPhasePreview;
  project: ProjectPreview;
  sourceId: number;
  sourceType: TimesheetUISourceType.Phase;
  eventId?: string;
}

export interface TimesheetCalendarIntegrationEntry
  extends TimesheetTimeEntry,
    RbacOptions {
  type: TimesheetEntryType.Integration;
  activityName: string;
  project: ProjectPreview;
  sourceId: string;
  sourceType: TimesheetCalendarSourceType;
  sourcePage?: TimesheetSourcePage;
}

export type TimesheetEntry =
  | TimesheetTaskEntry
  | TimesheetAdhocEntry
  | TimesheetProjectEntry
  | TimesheetPhaseEntry
  | Optional<TimesheetCalendarIntegrationEntry, 'project'>;

export type TimesheetAddFromCalendarEntry =
  | TimesheetCalendarIntegrationEntry
  | TimesheetTaskEntry
  | TimesheetPhaseEntry
  | TimesheetAdhocEntry;

export interface TimesheetFromAllocationsProjectEntry
  extends TimesheetTimeEntry,
    RbacOptions {
  sourcePage?: TimesheetSourcePage;
  project: ProjectPreview;
  sourceId: string;
  type: TimesheetEntryType.ProjectEntry;
}

export type TimesheetAddFromAllocationEntry =
  | TimesheetFromAllocationsProjectEntry
  | TimesheetTaskEntry
  | TimesheetPhaseEntry
  | TimesheetAdhocEntry;

export interface TimesheetProject {
  id: number;
  name?: string;
  logoUrl?: string;
  companyName?: string;
  deleted?: boolean;
  archived?: boolean;
}
export interface Timesheet {
  entries: TimesheetEntry[];
  reminders: TimesheetReminder[];
  status: TimesheetStatus;
  userId: number;
  user?: User;
  startDate: string;
  updatedAt: number;
  project: TimesheetProject;
  totalTime?: number;
}
interface BaseUserTimesheet {
  user: User;
  startDate: string;
  updatedAt: number;
  project: {
    id: number;
    name?: string;
  };
}

export interface ApprovedUserTimesheet extends BaseUserTimesheet {
  entries: TimesheetTableTimeEntry[];
  status: TimesheetStatus.Approved;
}

export interface SubmittedUserTimesheet extends BaseUserTimesheet {
  entries: TimesheetTableTimeEntry[];
  status: TimesheetStatus.Submitted;
}

export interface RejectedUserTimesheet extends BaseUserTimesheet {
  entries: TimesheetTableTimeEntry[];
  status: TimesheetStatus.Rejected;
}
export interface NonSubmittedUserTimesheet extends BaseUserTimesheet {
  entries: TimesheetTableTimeEntry[];
  status: TimesheetStatus.NotSubmitted;
  reminders: TimesheetReminder[];
}

export interface PhaseTaskPreview {
  project: ProjectPreview;
  projectPhase: ProjectPhasePreview;
  billable?: boolean;
  category?: TimesheetCategory;
}

export interface ProjectTask {
  project: CustomOption;
  billable?: boolean;
  category?: TimesheetCategory;
}

export type UserTimesheet =
  | NonSubmittedUserTimesheet
  | SubmittedUserTimesheet
  | RejectedUserTimesheet
  | ApprovedUserTimesheet;

export enum WorkType {
  GoogleCalendar = 'GOOGLE_CALENDAR',
  OutlookCalendar = 'OUTLOOK_CALENDAR',
  Task = 'TASK',
  Project = 'PROJECT',
  Phase = 'PHASE',
  Adhoc = 'ADHOC',
  Milestone = 'MILESTONE',
}

export enum NoteType {
  TimesheetReject = 'TIMESHEET_REJECT',
}

export enum NoteSubType {
  ProjectReject = 'PROJECT_REJECT',
  WorkReject = 'WORK_REJECT',
  OverallReject = 'OVERALL_REJECT',
}

export type RejectNoteType = {
  projectId: number;
  projectName: string;
  workType: WorkType;
  workId: number;
  workName: number;
  userId: number;
  noteId: number;
  noteText: string;
  type: NoteType;
  subType: NoteSubType;
  createdBy: number;
  createdAt: number;
  createdByUser?: User;
};

export type TimeEntryEditingMap = { [key: string]: boolean };

export type SetIsEditingTimeEntry = React.Dispatch<
  React.SetStateAction<TimeEntryEditingMap>
>;

interface EntryFromAllocationBaseResponse {
  date: string;
  userId: number;
  minutes: number;
  category?: TimesheetCategory;
  role: Role;
  team: Team;
  fields?: Array<InheritableFieldValue<FieldValueType>>;
  isInvalid: boolean;
  project: ProjectPreview;
  billable: boolean;
  marked?: boolean;
  allocationId: number;
  companyLogoUrlOfProject?: string;
  projectFinancialsBudgetId?: number;
}

interface EntryFromTaskAllocationResponse
  extends EntryFromAllocationBaseResponse {
  type: TimesheetEntryType.TaskEntry;
  task: TaskPreview;
}

interface EntryFromPhaseAllocationResponse
  extends EntryFromAllocationBaseResponse {
  type: TimesheetEntryType.PhaseEntry;
  projectPhase: ProjectPhasePreview;
}

interface EntryFromProjectAllocationResponse
  extends EntryFromAllocationBaseResponse {
  type: TimesheetEntryType.ProjectEntry;
}

export type EntryFromAllocationResponse =
  | EntryFromTaskAllocationResponse
  | EntryFromPhaseAllocationResponse
  | EntryFromProjectAllocationResponse;
